import React from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit"; // New import for edit icon

interface GroupInfoPanelProps {
  groupInfo: {
    name: string;
    description?: string;
    avatar?: string;
    photo?: string;
    members: { id: number; name: string; avatar?: string }[];
  };
  onClose: () => void;
  isDesktop: boolean;
  isAdmin?: boolean;
  onEditDescription?: () => void;
}

export const GroupInfoPanel: React.FC<GroupInfoPanelProps> = ({
  groupInfo,
  onClose,
  isDesktop,
  isAdmin = false,
  onEditDescription,
}) => {
  return (
    <Box
      sx={{
        position: isDesktop ? "relative" : "fixed",
        top: 0,
        right: 0,
        width: isDesktop ? "100%" : "100vw",
        height: "100%",
        bgcolor: "background.paper",
        zIndex: isDesktop ? 1 : 1300,
        boxShadow: isDesktop ? "none" : 3,
        overflowY: "auto",
      }}
    >
      {/* Back button for mobile */}
      {!isDesktop && (
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 16, left: 16 }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
          Group Info
        </Typography>
      </Box>

      {/* Group Avatar and Name */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
          px: 2,
        }}
      >
        <Avatar
          src={groupInfo.photo}
          sx={{ width: 120, height: 120, mb: 2, bgcolor: "primary.main" }}
        >
          {"O"}
        </Avatar>
        <Typography variant="h5" gutterBottom>
          {groupInfo.name}
        </Typography>
      </Box>

      {/* Description Section - NEW */}
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Description
          </Typography>
          {isAdmin && onEditDescription && (
            <IconButton
              size="small"
              onClick={onEditDescription}
              sx={{ ml: "auto" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="body1">
          {groupInfo.description || "No description available"}
        </Typography>
      </Box>

      <Divider />

      {/* Members Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {`Members (${groupInfo.members.length})`}
        </Typography>
        <List>
          {groupInfo.members.map((member) => (
            <ListItem key={member.id}>
              <ListItemAvatar>
                <Avatar src={member.avatar}>{member.name[0]}</Avatar>
              </ListItemAvatar>
              <ListItemText primary={member.name} />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default GroupInfoPanel;
